import React from "react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Catches render-time errors in any child route so a single broken page can't
 * silently take down the whole shell.
 *
 * Why a class component: React error boundaries only work as classes (no hook
 * equivalent yet). Keep this small and dependency-free so it never throws
 * itself.
 *
 * Usage: wrap `<Outlet />` (or any subtree) in `<RouteErrorBoundary>`. On error
 * we render the fallback below and ping Sentry. The user can click "Try again"
 * to reset state, or navigate to /app to leave the broken route.
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Best-effort Sentry ping. If Sentry isn't configured this is a no-op.
    try {
      Sentry.captureException(error, {
        contexts: {
          react: { componentStack: info?.componentStack || "" },
        },
      });
    } catch {
      /* never let logging crash the boundary */
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        data-testid="route-error-boundary"
      >
        <div className="max-w-lg w-full bg-white border border-[#E6E3DB] p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-[#FFF6F1] flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-[#C05C3D]" />
            </div>
            <p className="eyebrow text-[#C05C3D]">Something went sideways</p>
          </div>
          <h1 className="font-serif text-3xl text-[#2C302E] mb-3">
            We hit a snag rendering this page.
          </h1>
          <p className="text-sm text-[#4d4f4c] leading-relaxed">
            Your data is safe — this is just a hiccup on our end. Try refreshing,
            or head back to your dashboard. We've already been pinged about it.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C302E] text-white text-sm rounded-sm hover:bg-[#1b1e1c]"
              data-testid="route-error-retry"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
            <Link
              to="/app"
              onClick={this.reset}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#E6E3DB] text-[#2C302E] text-sm rounded-sm hover:bg-[#F7F5F0]"
              data-testid="route-error-home"
            >
              <Home className="h-4 w-4" />
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
